<script setup lang="ts">
import { useScrollLock } from '@vueuse/core'
import { onMounted, ref, watch } from 'vue'
import BlueProjectLogo from '../logos/BlueProjectLogo.vue'

defineProps({
  menu: {
    type: Object,
    required: false,
    default: () => ({ menu_items: [] }),
  },
  size: {
    type: String,
    required: false,
    default: 'lg',
  },
})

const showMenu = ref(false)
// Start with null for SSR compatibility
const target = ref<Window | null>(null)
const isLocked = useScrollLock(target)

// Only set window reference after component is mounted
onMounted(() => {
  target.value = window
})

// Helper functions
function lockScroll() {
  isLocked.value = true
}

function unlockScroll() {
  isLocked.value = false
}

watch(
  showMenu,
  (newValue) => {
    if (newValue) {
      lockScroll()
    }
    else {
      unlockScroll()
    }
  },
  { immediate: true },
)
</script>

<template>
  <div class="w-full" :class="{ 'h-[96px] md:h-[112px]': size === 'lg', 'h-[72px] md:h-[88px]': size === 'sm' }">
    <!-- Desktop Navgation -->
    <div class="xl:container absolute z-20 top-0 left-0 right-0" :class="{ 'py-6': size === 'lg', 'py-3': size === 'sm' }">
      <div class="mx-auto px-4 lg:px-16 flex items-center justify-between">
        <!-- Hamburger & Logo -->
        <div class="flex items-center space-x-2 sm:space-x-4">
          <!-- Hamburger -->
          <button class="realative z-50 xl:hidden" @click="showMenu = !showMenu">
            <Icon name="heroicons:bars-4-20-solid" class=" text-white w-8 h-8" />
          </button>

          <!-- Logo -->
          <div class="hover:-translate-y-0.5 duration-300 ease-out">
            <a href="/">
              <BlueProjectLogo fetchpriority="high" alt="Blue Project Logo" class="text-white h-12 md:h-16" />
            </a>
          </div>
        </div>

        <!-- Links -->
        <ul class="flex items-center sm:space-x-6 text-white uppercase ">
          <li
            v-for="menuItem in menu.menu_items" :key="menuItem.id" class="hidden xl:block hover:-translate-y-0.5 duration-300 ease-out font-bold "
            :class="{
              'text-base border-b-4': size === 'lg',
              'text-sm border-b-2': size === 'sm',
              'border-white': menuItem.permalink === $route.path,
              'border-transparent': menuItem.permalink !== $route.path,
            }"
          >
            <NuxtLink :to="menuItem.permalink" :target="menuItem.target">
              {{ menuItem.name }}
            </NuxtLink>
          </li>

          <!-- Donations -->
          <li
            class="sm:block hover:-translate-y-1 duration-300 ease-out pl-2 sm:pl-8"
          >
            <form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_top">
              <input type="hidden" name="cmd" value="_s-xclick">
              <input type="hidden" name="hosted_button_id" value="YE4AZWQ3FGBAU">
              <button
                type="submit" class="bg-blueproject-orange text-white rounded-xl border-[1px] border-white font-bold uppercase" :class="{
                  'text-sm xl:text-base py-2.5 px-2 sm:px-12': size === 'lg',
                  'text-sm py-2.5 px-2 sm:px-8': size === 'sm',
                }"
              >
                Donations
              </button>
              <img loading="lazy" alt="" border="0" src="https://www.paypal.com/en_US/i/scr/pixel.gif" width="1" height="1">
            </form>
          </li>
        </ul>
      </div>
    </div>

    <!-- Mobile Navigation -->
    <div
      class="fixed xl:hidden bg-white text-left z-50 top-0 bottom-0 left-0 w-full duration-300 ease-in-out"
      :class="{
        '-translate-x-full': !showMenu,
      }"
    >
      <!-- Logo & Close -->
      <div class="relative flex justify-between items-center m-4 z-20">
        <!-- Logo -->
        <a href="/">
          <BlueProjectLogo fetchpriority="high" alt="Blue Project Logo" class="text-glueblue-600 h-12 md:h-16 ml-2" />
        </a>

        <!-- Close -->
        <button class="absolute top-0 right-0" @click="showMenu = !showMenu">
          <Icon name="heroicons:x-mark-20-solid" class="text-glueblue-600 w-8 h-8" />
        </button>
      </div>

      <!-- Links -->
      <ul class="flex flex-col space-y-6 text-glueblue-600 uppercase mt-8 mx-8 z-20">
        <li v-for="menuItem in menu.menu_items" :key="menuItem.id" class="hover:-translate-y-0.5 duration-300 ease-out font-bold text-base">
          <a :href="menuItem.permalink" :target="menuItem.target">{{ menuItem.name }}</a>
        </li>

        <!-- Donations -->
        <div class="flex flex-col space-y-4">
          <form action="https://www.paypal.com/cgi-bin/webscr" method="post" target="_top">
            <input type="hidden" name="cmd" value="_s-xclick">
            <input type="hidden" name="hosted_button_id" value="YE4AZWQ3FGBAU">
            <button
              type="submit" class="bg-blueproject-orange text-white rounded  font-bold uppercase text-base p-4 px-12"
            >
              Donations
            </button>
            <img loading="lazy" alt="" border="0" src="https://www.paypal.com/en_US/i/scr/pixel.gif" width="1" height="1">
          </form>
        </div>
      </ul>

      <!-- Footer -->
      <div class="w-full absolute top-0 left-0 w-full h-full mx-8 z-10 pointer-events-none">
        <div class="absolute bottom-0 left-0 w-full">
          <!-- Social Media -->
          <ul class="flex justify-start text-xl pointer-events-auto">
            <li class="mr-4">
              <a
                class="text-glueblue-600"
                href="https://facebook.com/atlantisbahamas"
                target="_blank"
              >
                <Icon class="w-6 h-6" name="simple-icons:facebook" />
              </a>
            </li>
            <li class="mr-4">
              <a
                class="text-glueblue-600"
                href="https://www.youtube.com/atlantisbahamasofficial"
                target="_blank"
              >
                <Icon class="w-6 h-6" name="simple-icons:youtube" />
              </a>
            </li>
            <li class="mr-4">
              <a
                class="text-glueblue-600"
                href="https://www.pinterest.com/atlantisbahamas"
                target="_blank"
              >
                <Icon class="w-6 h-6" name="simple-icons:pinterest" />
              </a>
            </li>
            <li class="mr-4">
              <a
                class="text-glueblue-600"
                href="https://instagram.com/atlantisbahamas"
                target="_blank"
              >
                <Icon class="w-6 h-6" name="simple-icons:instagram" />
              </a>
            </li>
            <li class="mr-4">
              <a
                class="text-glueblue-600"
                href="https://twitter.com/atlantisbahamas"
                target="_blank"
              >
                <Icon class="w-6 h-6" name="simple-icons:x" />
              </a>
            </li>
          </ul>

          <!-- Logo -->
          <div class="flex justify-start my-8 pointer-events-auto">
            <a href="https://www.atlantisbahamas.com/" class="text-glueblue-600">
              <LogosAtlantisLogo style="width: 128px" />
            </a>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
